import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, LineChart, GitCompare, Map as MapIcon, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SearchBar } from "@/components/SearchBar";
import { CommunityCard } from "@/components/CommunityCard";
import { get, img, BRAND, COMPANY } from "@/lib/api";

const QUICK = [
  { label: "Florida", to: "/florida", icon: MapPin },
  { label: "California", to: "/california", icon: MapPin },
  { label: "Texas", to: "/texas", icon: MapPin },
  { label: "Arizona", to: "/arizona", icon: MapPin },
];

const FEATURES = [
  { icon: MapIcon, title: "Map every neighborhood", body: "See HOA communities on an interactive map and filter by what matters to you.", to: "/map", img: "p9716228" },
  { icon: GitCompare, title: "Compare side by side", body: "Line up fees, pet rules, rentals and amenities for up to three HOAs.", to: "/compare", img: "p9936969" },
  { icon: LineChart, title: "Track fee history", body: "Spot how dues have changed over time by state and community type.", to: "/fee-trends", img: "p7294672" },
];

const Hero = () => (
  <section className="relative min-h-[520px] sm:min-h-[620px] lg:min-h-[680px] flex items-center overflow-hidden" data-testid="home-hero">
    <img src={img("p5366406")} alt="Tree-lined suburban street with family homes" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#061330]/85 via-[#061330]/55 to-transparent" aria-hidden />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-12 pb-20 sm:py-20">
      <div className="max-w-2xl fade-up">
        <p className="text-xs uppercase font-semibold tracking-[0.2em] text-[#63D8C4]">Nationwide HOA Directory</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
          Know the rules <span className="text-[#63D8C4]">&amp; costs</span> before you move in.
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-200 max-w-xl leading-relaxed">
          Search HOA fees, pet policies, rental restrictions and amenities for neighborhoods across the United States.
        </p>
      </div>
      <div className="relative z-30 mt-7 sm:mt-10 max-w-3xl fade-up" style={{ animationDelay: "120ms" }}><SearchBar /></div>
      <div className="mt-4 sm:mt-6 flex flex-wrap gap-2 fade-up" style={{ animationDelay: "220ms" }}>
        {QUICK.map((q) => (
          <Link key={q.to} to={q.to} data-testid={`quick-filter-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-white hover:bg-white hover:text-[#0B2150] transition-colors">
            <q.icon className="h-4 w-4" aria-hidden />{q.label}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Stats = ({ s }) => (
  <section className="relative -mt-12 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Directory statistics">
    <dl className="grid grid-cols-2 lg:grid-cols-4 rounded-2xl bg-white border border-[#E2E8F0] shadow-xl divide-x divide-y lg:divide-y-0 divide-[#E2E8F0]">
      {[["Communities listed", s?.total?.toLocaleString()], ["Cities covered", s?.cities?.toLocaleString()],
        ["States + DC", s?.states], ["With websites", s?.with_website?.toLocaleString()]].map(([k, v]) => (
        <div key={k} className="p-4 sm:p-6 lg:p-8" data-testid={`stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <dt className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{k}</dt>
          <dd className="mt-1 sm:mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B2150]">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export default function Home() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => get("/stats") });
  const { data: states = [] } = useQuery({ queryKey: ["states"], queryFn: () => get("/states") });
  const { data: articles = [] } = useQuery({ queryKey: ["articles"], queryFn: () => get("/articles") });
  const topStates = [...states].sort((a, b) => b.count - a.count).slice(0, 12);
  const ld = [
    { "@context": "https://schema.org", "@type": "Organization", name: BRAND, legalName: COMPANY, url: window.location.origin, logo: `${window.location.origin}/logo.png` },
    { "@context": "https://schema.org", "@type": "WebSite", name: BRAND, url: window.location.origin,
      potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/search?q={search_term_string}`, "query-input": "required name=search_term_string" } },
  ];

  return (
    <div data-testid="home-page">
      <Seo path="/" jsonLd={ld} />
      <Hero />
      <Stats s={stats} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24">
        <div className="flex items-end justify-between gap-4 mb-6 sm:mb-10">
          <div>
            <p className="eyebrow">Featured communities</p>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0B2150]">Neighborhoods people are exploring</h2>
          </div>
          <Link to="/search" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#027A6D] hover:text-[#0B2150]" data-testid="home-view-all-link">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(stats?.featured || []).map((c) => <CommunityCard key={c.id} c={c} />)}
        </div>
      </section>

      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24 grid gap-4 sm:gap-6 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Link key={f.to} to={f.to} data-testid={`home-feature-${f.to.slice(1)}`}
              className={`group relative overflow-hidden rounded-3xl ${i === 0 ? "lg:row-span-2 min-h-[260px] sm:min-h-[420px]" : "min-h-[180px] sm:min-h-[200px]"} flex items-end`}>
              <img src={img(f.img)} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#061330]/90 via-[#061330]/40 to-transparent" aria-hidden />
              <div className="relative p-6 sm:p-8 text-white">
                <f.icon className="h-8 w-8 text-[#63D8C4]" aria-hidden />
                <h3 className="mt-3 text-xl sm:text-2xl font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-200 max-w-sm">{f.body}</p>
              </div>
            </Link>
          ))}
          <div className="lg:col-span-2 rounded-3xl bg-[#0B2150] text-white p-6 sm:p-8 lg:p-10 relative overflow-hidden grain">
            <p className="text-xs uppercase font-semibold tracking-[0.2em] text-[#B0E090]">Board member or resident?</p>
            <h3 className="mt-3 text-2xl sm:text-3xl font-semibold max-w-lg">Add or update your community's HOA details.</h3>
            <p className="mt-3 text-slate-300 max-w-lg text-sm">Help buyers and neighbors with accurate fees and rules. Submissions are reviewed before publishing.</p>
            <Link to="/submit-hoa" data-testid="home-submit-cta" className="relative z-10 mt-6 inline-flex items-center gap-2 rounded-full bg-[#03A896] px-6 py-3 font-semibold hover:bg-[#63D8C4] hover:text-[#061330] transition-colors">Submit an HOA <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24">
        <p className="eyebrow">Browse by state</p>
        <h2 className="mt-2 mb-6 sm:mb-10 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0B2150]">Most-listed states</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {topStates.map((s) => (
            <Link key={s.slug} to={`/${s.slug}`} data-testid={`home-state-${s.slug}`}
              className="group rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-5 hover:border-[#03A896] hover:shadow-lg transition-[box-shadow,border-color]">
              <span className="font-display text-2xl sm:text-3xl font-bold text-[#E2E8F0] group-hover:text-[#63D8C4] transition-colors">{s.code}</span>
              <p className="mt-2 font-semibold text-[#0B2150]">{s.state}</p>
              <p className="text-xs text-slate-600">{s.count} HOAs · {s.city_count} cities</p>
            </Link>
          ))}
        </div>
        <Link to="/states" className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-[#027A6D] hover:text-[#0B2150]" data-testid="home-all-states-link">See all states <ArrowRight className="h-4 w-4" /></Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex items-end justify-between gap-4 mb-6 sm:mb-10">
          <div>
            <p className="eyebrow">Learn</p>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0B2150]">HOA guides & articles</h2>
          </div>
          <Link to="/articles" className="text-sm font-semibold text-[#027A6D] hover:text-[#0B2150]" data-testid="home-articles-link">All articles</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {articles.slice(0, 3).map((a) => (
            <Link key={a.slug} to={`/articles/${a.slug}`} className="group" data-testid={`home-article-${a.slug}`}>
              <div className="overflow-hidden rounded-2xl h-44 sm:h-52"><img src={img(a.image)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#027A6D]">{a.category}</p>
              <h3 className="mt-1 text-lg font-semibold text-[#0B2150] group-hover:underline">{a.title}</h3>
              <p className="mt-1 text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
